/**
 * Which firm-console pages a role can use, decided by the tier the server checks
 * on each page's own reads.
 *
 * WHY THIS EXISTS (4 Oct 2026, round 11 round 10). The /admin route gate admits
 * every firm-staff role to every /admin path; only the sidebar hid the pages a
 * role cannot use. A relationship manager who typed /admin/billing landed on a
 * screen whose every read the server refused, so the page was a stack of 403
 * cards and console errors. The gate now asks this file first and shows a plain
 * "not available to your role" page instead of mounting a screen that can only
 * fail.
 *
 * EVERY /admin ROUTE HAS A LINE BELOW, including the ones open to all firm
 * staff, and `admin-route-access.test.ts` fails when the router gains an /admin
 * route this list does not decide. A page cannot fall through to "open" by being
 * forgotten.
 *
 * The tier for each page is the one on that page's primary read, from the
 * handler that serves it (named beside each line). The predicates are the
 * frontend's mirror of backend/src/lib/tenant-roles.ts, held to it by
 * `firm-capabilities.test.ts`. The SIDEBAR is separate and unchanged: for a
 * director it is narrower than the server on some pages, which is an open
 * decision for Paul, and a typed URL here follows the server.
 */

import {
  canFileFicReports,
  canManageExternalAuditors,
  canReadFirmSettings,
  canReadWholeBook,
  canUseAdminOrComplianceTier,
  canUseWorklistTier,
  canViewLegalHolds,
  isPlatformOperator,
} from './firm-capabilities';

type Check = (role: string | null | undefined) => boolean;

/** Every firm-staff role reaches it (the /admin gate has already checked that). */
const ANY_FIRM_STAFF: Check = () => true;

export interface AdminRouteDecision {
  /** The route as `routeTree.gen.ts` spells it. */
  readonly path: string;
  /** The page's name as the console labels it, for the refusal page. */
  readonly name: string;
  readonly allowed: Check;
}

export const ADMIN_ROUTE_DECISIONS: readonly AdminRouteDecision[] = [
  // Bare /admin redirects to the dashboard.
  { path: '/admin', name: 'Firm console', allowed: ANY_FIRM_STAFF },
  { path: '/admin/dashboard', name: 'Dashboard', allowed: ANY_FIRM_STAFF },
  // firm-clients-list: tenant-wide readers, and rm scoped to its own clients.
  { path: '/admin/client-book', name: 'Clients', allowed: ANY_FIRM_STAFF },
  // Older paths into the same book; each redirects.
  { path: '/admin/clients', name: 'Clients', allowed: ANY_FIRM_STAFF },
  { path: '/admin/clients/$id', name: 'Clients', allowed: ANY_FIRM_STAFF },
  { path: '/admin/trusts', name: 'Clients', allowed: ANY_FIRM_STAFF },
  // My queue: its reads are gated per tier inside the page.
  { path: '/admin/queue', name: 'My queue', allowed: ANY_FIRM_STAFF },
  // FICA pipeline: risk-review (worklist) and the scoped trusts list.
  { path: '/admin/fica', name: 'FICA pipeline', allowed: ANY_FIRM_STAFF },
  // audit-log-list: any member of the tenant, filtered for FIC tipping-off.
  { path: '/admin/audit', name: 'Audit log', allowed: ANY_FIRM_STAFF },
  // team-list.ts:169: isFirmStaffMember, so every staff role reads the roster
  // (full contact detail only for the owner-admin tier, :175). What an rm can
  // DO here is nothing: inviting (team-invite.ts:271) and removing
  // (team-remove.ts:161) are isTenantManager, re-sending an invitation
  // (admin-team-invite-send.ts:236) the owner-admin tier. The page hides those
  // controls by the same predicates (canManageStaff, canSendStaffInvite).
  { path: '/admin/team', name: 'Team', allowed: ANY_FIRM_STAFF },
  // admin-risk-review-list: isTenantWorklistParticipant.
  { path: '/admin/risk-review', name: 'Risk review', allowed: canUseWorklistTier },
  // edd-cases: EDD_WORKER_AUTHORISED_ROLES, the worklist membership.
  { path: '/admin/edd', name: 'PEP / EDD', allowed: canUseWorklistTier },
  { path: '/admin/edd/$id', name: 'PEP / EDD', allowed: canUseWorklistTier },
  // aes-sync-log-list, integrations-list: isTenantWorklistParticipant.
  { path: '/admin/aes-sync-log', name: 'AES sync log', allowed: canUseWorklistTier },
  { path: '/admin/integrations', name: 'Integrations', allowed: canUseWorklistTier },
  // admin-billing-get, admin-branding-get: isTenantAdminOrCompliance (READ).
  { path: '/admin/billing', name: 'Billing', allowed: canReadFirmSettings },
  { path: '/admin/branding', name: 'Branding studio', allowed: canReadFirmSettings },
  { path: '/admin/branding/custom-domain', name: 'Branding studio', allowed: canReadFirmSettings },
  // admin-dsar-list, admin-entity-graph-get, admin-master-lodgements,
  // firm-compliance-reports-list: isTenantAdminOrCompliance.
  { path: '/admin/dsar', name: 'Data requests', allowed: canUseAdminOrComplianceTier },
  { path: '/admin/entity-graph', name: 'Entity graph', allowed: canUseAdminOrComplianceTier },
  { path: '/admin/master-lodgements', name: 'Master ICMS', allowed: canUseAdminOrComplianceTier },
  { path: '/admin/compliance-reports', name: 'FICA reporting', allowed: canUseAdminOrComplianceTier },
  // partner-commissions: isTenantAdminOrCompliance for a firm.
  { path: '/admin/partner-earnings', name: 'Partner earnings', allowed: canUseAdminOrComplianceTier },
  // firm-documents-list, firm-document-coverage-get: isTenantWideReader.
  { path: '/admin/documents', name: 'Documents', allowed: canReadWholeBook },
  { path: '/admin/document-coverage', name: 'Doc coverage', allowed: canReadWholeBook },
  // admin-external-auditors-list: isTenantAdmin.
  { path: '/admin/external-auditors', name: 'External auditors', allowed: canManageExternalAuditors },
  // fic-reports-list: FIC_AUTHORISED_ROLES.
  { path: '/admin/fic-reports', name: 'FIC reports', allowed: canFileFicReports },
  { path: '/admin/fic-reports/new', name: 'FIC reports', allowed: canFileFicReports },
  { path: '/admin/fic-reports/$id', name: 'FIC reports', allowed: canFileFicReports },
  // legal-holds-list: isLegalHoldViewer.
  { path: '/admin/legal-holds', name: 'Legal holds', allowed: canViewLegalHolds },
  // admin-recovery-requests-list: isPlatformSuperAdmin.
  { path: '/admin/recovery-requests', name: 'Recovery requests', allowed: isPlatformOperator },
];

/** A route template segment like `$id` matches any one concrete segment. */
function matches(template: string, pathname: string): boolean {
  const t = template.split('/').filter(Boolean);
  const p = pathname.split('/').filter(Boolean);
  if (p.length < t.length) return false;
  for (let i = 0; i < t.length; i++) {
    if (t[i]!.startsWith('$')) continue;
    if (t[i] !== p[i]) return false;
  }
  return true;
}

/**
 * Can `role` use the console page at `pathname`? The most specific decision
 * wins (a page and its sub-pages are decided together unless a sub-page has its
 * own line). An /admin path with no decision is open to firm staff, which the
 * coverage test keeps from happening by accident.
 */
export function adminRouteAccess(
  pathname: string,
  role: string | null | undefined,
): { allowed: true } | { allowed: false; name: string } {
  let best: AdminRouteDecision | null = null;
  let bestDepth = -1;
  for (const d of ADMIN_ROUTE_DECISIONS) {
    if (!matches(d.path, pathname)) continue;
    const depth = d.path.split('/').filter(Boolean).length;
    if (depth > bestDepth) {
      best = d;
      bestDepth = depth;
    }
  }
  if (!best || best.allowed(role)) return { allowed: true };
  return { allowed: false, name: best.name };
}
